import { Accordion } from "@ark-ui/solid";
import { isEmpty, isNil, noop } from "lodash-es";
import type { VoidComponent } from "solid-js";
import { createMemo, For, Index, Show } from "solid-js";
import { IconTile } from "../../../../components/icon/IconTile";
import { PillSingleSelect } from "../../../../components/menu/Select";
import type { Grant } from "../../../../state/grant";
import type { Group } from "../../../../state/group";
import { useTranslation } from "../../../../state/i18n";
import type { Role } from "../../../../state/role";
import { themeIconColour } from "../../../../types";
import type { Folder } from "../../../../types/Folder";
import type { Form } from "../../../../state/form";
import type { SelectOption } from "../../../../types/Menu";
import { getGroupsForUser } from "../../../../utils/groupUtil";
import type { GroupedRoles } from "../../../../utils/roleUtil";
import { filterMixedRolesForScope, getRoleName, toGroupedRoleOptions, toRoleByIdMap } from "../../../../utils/roleUtil";
import Text from "../../../../components/text/Text";
import type { ResourceAccess } from "../UserPage";
import { GroupsPopover } from "./GroupsPopover";
import type RoleId from "../../../../state/RoleId";
import type FolderId from "../../../../state/FolderId";
import type FormId from "../../../../state/FormId";
import { Icon } from "../../../../components/icon/Icon";
import type { User } from "../../../../state/user";
import type { Invite } from "../../../../state/invite";

type FolderAccordionProps = {
  folders: Folder[];
  roles: Role[];
  groupedRoles: GroupedRoles;
  grants: Grant[];
  groups: Group[];
  user?: User | Invite;
  selectFolderRole?: (roleId: RoleId | null, folderId: FolderId, currentRoleId: RoleId | null) => void;
  selectFormRole: (roleId: RoleId | null, formId: FormId, currentRoleId: RoleId | null) => void;
};

type FormRowProps = {
  form: Form;
  access: ResourceAccess[];
  options: SelectOption<RoleId | null>[];
  selectFormRole: (roleId: RoleId | null, formId: FormId, currentRoleId: RoleId | null) => void;
};

const FormRow: VoidComponent<FormRowProps> = (props) => {
  const { t } = useTranslation();
  const directAccess = createMemo(() => props.access.find((a) => a.direct));
  const inheritedAccess = createMemo(() => props.access.filter((a) => !isEmpty(a.groups)));
  const inheritedSections = createMemo(() =>
    inheritedAccess().map((section) => ({ label: getRoleName(section.role, t), groups: section.groups })),
  );

  return (
    <div class="flex items-center gap-2 pl-9">
      <IconTile
        icon={props.form.meta.icon}
        size="sm"
        border
        class="shrink-0"
        color={!isNil(props.form.meta.theme) ? themeIconColour[props.form.meta.theme.name] : "icon-brand"}
      />
      <Text text={props.form.meta.name} as="span" class="min-w-0 grow text-base-medium text-primary" truncate />
      <Show when={props.form.status === "HIDDEN"}>
        <span class="shrink-0 rounded-sm px-1.5 py-0.5 text-small-regular text-secondary">
          {t(($) => $.USER_PAGE_FORM_HIDDEN)}
        </span>
      </Show>
      <Show when={!isEmpty(inheritedAccess())}>
        <div class="flex shrink-0 items-center gap-2">
          <GroupsPopover
            title={t(($) => $.USER_PAGE_INHERITED_ROLES, { count: inheritedAccess().length })}
            popoverTitle={t(($) => $.USER_PAGE_INHERITED_PERMISSIONS)}
            sections={inheritedSections()}
          />
          <Text text={t(($) => $.USER_PAGE_AND)} class="text-base-regular text-secondary" />
        </div>
      </Show>
      <div class="shrink-0">
        <PillSingleSelect
          ariaLabel={t(($) => $.USER_PAGE_RESOURCE_ROLE_ARIA, { name: props.form.meta.name })}
          options={props.options}
          value={directAccess()?.role.id ?? null}
          onSelect={(roleId) => props.selectFormRole(roleId, props.form.id, directAccess()?.role.id ?? null)}
          disabled={false}
        />
      </div>
    </div>
  );
};

type FolderItemProps = {
  folder: Folder;
  accessByResource: Map<string, ResourceAccess[]>;
  folderOptions: SelectOption<RoleId | null>[];
  formOptions: SelectOption<RoleId | null>[];
  selectFolderRole?: (roleId: RoleId | null, folderId: FolderId, currentRoleId: RoleId | null) => void;
  selectFormRole: (roleId: RoleId | null, formId: FormId, currentRoleId: RoleId | null) => void;
};

const FolderItem: VoidComponent<FolderItemProps> = (props) => {
  const { t } = useTranslation();
  const folderAccess = createMemo(() => props.accessByResource.get(props.folder.id.toString()) ?? []);
  const directFolderAccess = createMemo(() => folderAccess().find((a) => a.direct));
  const inheritedAccess = createMemo(() => folderAccess().filter((a) => !isEmpty(a.groups)));
  const inheritedSections = createMemo(() =>
    inheritedAccess().map((section) => ({ label: getRoleName(section.role, t), groups: section.groups })),
  );
  const hasFormAccess = createMemo(() =>
    props.folder.forms.some((form) => (props.accessByResource.get(form.id.toString()) ?? []).some((a) => a.direct)),
  );
  const folderOptions = createMemo(() =>
    hasFormAccess()
      ? props.folderOptions.map((option) =>
          isNil(option.value) ? { ...option, label: t(($) => $.USER_PAGE_ACCESS_VIA_FORMS) } : option,
        )
      : props.folderOptions,
  );

  return (
    <Accordion.Item value={props.folder.id.toString()}>
      <div class="relative border-b border-subtle">
        <Accordion.ItemTrigger
          class="absolute inset-0 rounded-sm hover:bg-surface-hover focus-visible:outline active:bg-surface-pressed"
          aria-label={t(($) => $.USER_PAGE_FOLDER_TOGGLE_ARIA, { name: props.folder.meta.name })}
        />
        <div class="pointer-events-none relative flex w-full items-center gap-3 py-4 pr-2 pl-1">
          <Accordion.ItemIndicator class="size-5 shrink-0 rotate-0 transition-transform duration-200 ease-linear open:rotate-90">
            <Icon name="chevronRight" type="solid" size="lg" class="icon-secondary" />
          </Accordion.ItemIndicator>
          <IconTile icon={props.folder.meta.icon} size="md" border class="shrink-0" color="icon-tertiary" />
          <div class="flex min-w-0 grow flex-col truncate text-left">
            <Text as="h6" class="text-base-medium text-primary" text={props.folder.meta.name} truncate />
            <Show when={props.folder.meta.description}>
              {(description) => <Text as="p" class="text-base-regular text-secondary" text={description()} truncate />}
            </Show>
          </div>
          <Show when={!isEmpty(inheritedAccess())}>
            <div class="pointer-events-auto flex shrink-0 items-center gap-2">
              <GroupsPopover
                title={t(($) => $.USER_PAGE_INHERITED_ROLES, { count: inheritedAccess().length })}
                popoverTitle={t(($) => $.USER_PAGE_INHERITED_PERMISSIONS)}
                sections={inheritedSections()}
              />
              <Text text={t(($) => $.USER_PAGE_AND)} class="text-base-regular text-secondary" />
            </div>
          </Show>
          <div class="pointer-events-auto shrink-0">
            <Show
              when={props.selectFolderRole}
              fallback={
                <PillSingleSelect
                  options={[{ variant: "text", label: t(($) => $.USER_PAGE_FOLDER_MANAGER), value: "foldermanager" }]}
                  value="foldermanager"
                  ariaLabel={t(($) => $.USER_PAGE_RESOURCE_ROLE_ARIA, { name: props.folder.meta.name })}
                  onSelect={noop}
                  disabled={true}
                />
              }
            >
              {(selectFolderRole) => (
                <PillSingleSelect
                  ariaLabel={t(($) => $.USER_PAGE_RESOURCE_ROLE_ARIA, { name: props.folder.meta.name })}
                  options={folderOptions()}
                  value={directFolderAccess()?.role.id ?? null}
                  onSelect={(roleId) =>
                    selectFolderRole()(roleId, props.folder.id, directFolderAccess()?.role.id ?? null)
                  }
                />
              )}
            </Show>
          </div>
        </div>
      </div>
      {/* oxlint-disable better-tailwindcss/no-restricted-classes */}
      <Accordion.ItemContent class="grid grid-rows-[1fr] border-b border-subtle transition-all duration-250 ease-out closed:grid-rows-[0fr] closed:border-b-0 closed:duration-200 closed:ease-in">
        <Accordion.ItemContext>
          {(context) => {
            // Large customers can have thousands of forms; only render them once the folder has been expanded.
            // The content wrapper stays mounted so the first expand still animates.
            const hasExpanded = createMemo((expanded: boolean) => expanded || context().expanded, false);

            return (
              <div class="overflow-hidden">
                <div class="flex flex-col gap-6 py-4 pl-1" inert={!context().expanded}>
                  <Show when={hasExpanded()}>
                    <For
                      each={props.folder.forms.filter(
                        (form) =>
                          form.status === "ACTIVE" ||
                          form.status === "HIDDEN" ||
                          props.accessByResource.has(form.id.toString()),
                      )}
                      fallback={
                        <div class="pl-8 text-medium-regular text-secondary">
                          {t(($) => $.USER_PAGE_NO_FORMS_IN_FOLDER)}
                        </div>
                      }
                    >
                      {(form) => (
                        <FormRow
                          form={form}
                          access={props.accessByResource.get(form.id.toString()) ?? []}
                          options={props.formOptions}
                          selectFormRole={props.selectFormRole}
                        />
                      )}
                    </For>
                  </Show>
                </div>
              </div>
            );
          }}
        </Accordion.ItemContext>
      </Accordion.ItemContent>
    </Accordion.Item>
  );
};

export const FolderAccordion: VoidComponent<FolderAccordionProps> = (props) => {
  const { t } = useTranslation();

  const accessByResource = createMemo(() => {
    const roleById = toRoleByIdMap(props.roles);

    const byResourceAndRole = new Map<string, { resourceId: string; role: Role; groups: Group[]; direct: boolean }>();
    const addGrant = (grant: Grant, group?: Group) => {
      if (grant.resourceType !== "FORM" && grant.resourceType !== "FOLDER") {
        return;
      }
      const role = roleById.get(grant.roleId.toString());
      if (isNil(role)) {
        return;
      }
      const key = `${grant.resourceId.toString()}::${grant.roleId.toString()}`;
      const existing = byResourceAndRole.get(key);
      if (isNil(existing)) {
        byResourceAndRole.set(key, {
          resourceId: grant.resourceId.toString(),
          role,
          groups: isNil(group) ? [] : [group],
          direct: isNil(group),
        });
      } else if (isNil(group)) {
        existing.direct = true;
      } else if (!existing.groups.some((existingGroup) => existingGroup.id.equals(group.id))) {
        existing.groups.push(group);
      }
    };

    props.grants.forEach((grant) => addGrant(grant));

    if (!isNil(props.user)) {
      getGroupsForUser(props.user, props.groups).forEach((group) =>
        group.grants.forEach((grant) => addGrant(grant, group)),
      );
    }

    const byResource = new Map<string, ResourceAccess[]>();
    byResourceAndRole.forEach(({ resourceId, role, groups, direct }) => {
      byResource.set(resourceId, [...(byResource.get(resourceId) ?? []), { role, groups, direct }]);
    });
    return byResource;
  });
  const roleOptions = (roles: Role[], noAccessDescription: string) => [
    {
      variant: "text" as const,
      label: t(($) => $.USER_PAGE_NO_ROLE),
      description: noAccessDescription,
      value: null,
    },
    ...toGroupedRoleOptions(
      roles,
      t,
      t(($) => $.ADD_PERMISSION_MODAL_CUSTOM_ROLE_LABEL),
    ),
  ];
  // Shared by every folder and form row, so build them once instead of per row
  const folderOptions = createMemo(() =>
    roleOptions(
      [...props.groupedRoles.folderRoles, ...filterMixedRolesForScope(props.roles, "FOLDER")],
      t(($) => $.USER_PAGE_NO_FOLDER_ACCESS_DESCRIPTION),
    ),
  );
  const formOptions = createMemo(() =>
    roleOptions(
      [...props.groupedRoles.formRoles, ...filterMixedRolesForScope(props.roles, "FORM")],
      t(($) => $.USER_PAGE_NO_FORM_ACCESS_DESCRIPTION),
    ),
  );

  return (
    <Accordion.Root multiple>
      <Index each={props.folders}>
        {(folder) => (
          <FolderItem
            folder={folder()}
            accessByResource={accessByResource()}
            folderOptions={folderOptions()}
            formOptions={formOptions()}
            selectFolderRole={props.selectFolderRole}
            selectFormRole={props.selectFormRole}
          />
        )}
      </Index>
    </Accordion.Root>
  );
};
